import React, { useEffect, useState } from "react";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import CardMembershipIcon from "@mui/icons-material/CardMembership";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import BusinessIcon from "@mui/icons-material/Business";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CakeOutlinedIcon from "@mui/icons-material/CakeOutlined";
import WcOutlinedIcon from "@mui/icons-material/WcOutlined";
import dayjs from "dayjs";
import MemberPortalGuard from "./MemberPortalGuard";
import { getMemberProfile } from "../../api/memberPortalClient";
import Popup from "../../base-components/Popup";
import {
  SectionCard,
  LoadingSkeleton,
  StatusChip,
  accountBalanceDisplay,
  initials,
} from "./components/MemberUI";

function Field({ icon, label, value }) {
  return (
    <div className="flex gap-3 py-3 border-b border-gx-border last:border-0">
      <div className="w-9 h-9 rounded-lg bg-gx-canvas text-gx-navy flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-wide font-semibold text-gx-ink-soft">
          {label}
        </p>
        <p className="text-sm font-medium text-gx-ink mt-0.5 break-words">
          {value || "—"}
        </p>
      </div>
    </div>
  );
}

function MemberProfileContent() {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState("");
  const [popupOpen, setPopupOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { ok, body } = await getMemberProfile();
        if (cancelled) return;
        if (!ok) {
          setError(body?.error?.message || "Failed to load profile.");
          setPopupOpen(true);
          return;
        }
        setProfile(body.data);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Failed to load profile.");
          setPopupOpen(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return <LoadingSkeleton variant="profile" />;
  }

  const accountBalance = accountBalanceDisplay(profile?.balance);

  return (
    <div className="p-4 md:p-6 space-y-5">
      <div className="gx-glass rounded-gx p-6 md:p-8 flex flex-col sm:flex-row sm:items-center gap-5 border-l-[3px] border-l-gx-red">
        <div className="w-20 h-20 rounded-gx bg-gx-red/10 text-gx-red flex items-center justify-center text-3xl font-bold">
          {initials(profile?.name)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl md:text-3xl font-bold text-gx-ink truncate tracking-tight">
              {profile?.name || "My Profile"}
            </h1>
            <StatusChip
              active={profile?.is_active !== false}
              label={profile?.is_active !== false ? "Active" : "Inactive"}
            />
          </div>
          <p className="text-gx-ink-soft text-sm mt-1">
            {profile?.member_code || "—"}
            {profile?.location?.name ? ` · ${profile.location.name}` : ""}
          </p>
          {profile?.package?.title ? (
            <p className="text-gx-navy text-sm mt-2 inline-flex items-center gap-1.5 bg-gx-canvas border border-gx-border px-3 py-1 rounded-full">
              <CardMembershipIcon sx={{ fontSize: 16 }} />
              {profile.package.title}
              {profile.package.duration
                ? ` · ${profile.package.duration}`
                : ""}
            </p>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <SectionCard title="Personal details" icon={<PersonOutlineIcon />}>
          <Field
            icon={<PersonOutlineIcon fontSize="small" />}
            label="Full name"
            value={profile?.name}
          />
          <Field
            icon={<BadgeOutlinedIcon fontSize="small" />}
            label="Member code"
            value={profile?.member_code}
          />
          <Field
            icon={<BadgeOutlinedIcon fontSize="small" />}
            label="CNIC"
            value={profile?.cnic}
          />
          <Field
            icon={<WcOutlinedIcon fontSize="small" />}
            label="Gender"
            value={profile?.gender}
          />
          <Field
            icon={<CakeOutlinedIcon fontSize="small" />}
            label="Date of birth"
            value={
              profile?.date_of_birth
                ? dayjs(profile.date_of_birth).format("MMM DD, YYYY")
                : null
            }
          />
        </SectionCard>

        <SectionCard title="Contact" icon={<PhoneOutlinedIcon />}>
          <Field
            icon={<PhoneOutlinedIcon fontSize="small" />}
            label="Phone"
            value={profile?.phone}
          />
          <Field
            icon={<EmailOutlinedIcon fontSize="small" />}
            label="Email"
            value={profile?.email}
          />
          <Field
            icon={<HomeOutlinedIcon fontSize="small" />}
            label="Address"
            value={profile?.address}
          />
          <Field
            icon={<PlaceOutlinedIcon fontSize="small" />}
            label="City"
            value={profile?.city}
          />
        </SectionCard>

        <SectionCard title="Membership" icon={<CardMembershipIcon />}>
          <Field
            icon={<CardMembershipIcon fontSize="small" />}
            label="Package"
            value={
              profile?.package?.title
                ? `${profile.package.title}${
                    profile.package.duration
                      ? ` (${profile.package.duration})`
                      : ""
                  }`
                : null
            }
          />
          <Field
            icon={<PlaceOutlinedIcon fontSize="small" />}
            label="Club / Location"
            value={profile?.location?.name}
          />
          <Field
            icon={<BusinessIcon fontSize="small" />}
            label="Company"
            value={
              profile?.company?.company_name || profile?.company?.name
            }
          />
          <Field
            icon={<FitnessCenterIcon fontSize="small" />}
            label="Trainer"
            value={profile?.trainer?.name || "Not assigned"}
          />
        </SectionCard>

        <SectionCard title="Account" icon={<AccountBalanceWalletIcon />}>
          <Field
            icon={<AccountBalanceWalletIcon fontSize="small" />}
            label={accountBalance.label}
            value={`${accountBalance.value} · ${accountBalance.hint}`}
          />
          <Field
            icon={<BadgeOutlinedIcon fontSize="small" />}
            label="Status"
            value={profile?.is_active ? "Active" : "Inactive"}
          />
          {profile?.is_trainer && profile?.trainerEnd ? (
            <Field
              icon={<FitnessCenterIcon fontSize="small" />}
              label="Trainer valid till"
              value={dayjs(profile.trainerEnd).format("MMM DD, YYYY")}
            />
          ) : null}
        </SectionCard>
      </div>

      {popupOpen && (
        <Popup
          alertType="error"
          message={error}
          onClose={() => setPopupOpen(false)}
        />
      )}
    </div>
  );
}

function MemberProfile() {
  return (
    <MemberPortalGuard>
      <MemberProfileContent />
    </MemberPortalGuard>
  );
}

export default MemberProfile;
